import { useQuery } from '@tanstack/react-query';
import { Show } from '../../types/Show';
import { ApiService } from '../../services/apiService';
import { cachingStrategyService, DataType } from '../../services/cachingStrategyService';
import { debugLog } from '../../utils/debugLog';

export const NEW_SHOW_DETECTION_QUERY_KEY = ['shows', 'newShowDetection'] as const;

export interface NewShowDetectionOptions {
  station_id?: string;
  enabled?: boolean;
}

export interface NewShowDetectionReturn {
  recentShows: Show[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<any>;
}

/**
 * Hook for detecting new show recordings by fetching only the last 2 shows
 * 
 * This hook is specifically designed for LiveShowHero's new show detection feature.
 * It fetches only the most recent 2 shows to minimize API calls and prevent
 * conflicts with ShowsPage's infinite scroll pagination.
 * 
 * Features:
 * - Fetches only last 2 shows for efficiency
 * - Separate cache key to avoid conflicts with useShowsData
 * - Optimized for new show detection use case
 * - Lightweight and fast
 * 
 * @param options Configuration options for the hook
 * @returns Recent shows data for new show detection
 */
export const useNewShowDetection = (options: NewShowDetectionOptions = {}): NewShowDetectionReturn => {
  const {
    station_id = 'eruption',
    enabled = true
  } = options;

  // Use shorter cache time since we need fresh data for new show detection
  const cacheStrategy = cachingStrategyService.getStrategy(DataType.PAST_SHOWS_DATA) || {
    staleTime: 2 * 60 * 1000, // 2 minutes - shorter for new show detection
    gcTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: false,
    refetchOnReconnect: true,
    retry: 3,
    retryDelay: 1000,
  };

  const { 
    data: recentShows = [], 
    isPending, 
    isError,
    error, 
    refetch 
  } = useQuery({
    queryKey: [...NEW_SHOW_DETECTION_QUERY_KEY, station_id, 2], // Include limit in cache key
    queryFn: async (): Promise<Show[]> => {
      debugLog('🔍 useNewShowDetection: Fetching last 2 shows for new show detection');
      
      // Fetch only the last 2 shows using the paginated API
      const response = await ApiService.getShowsPaginated({
        station_id,
        limit: 2, // Only need last 2 shows for comparison
        // No filters - we want the most recent shows regardless
      });
      
      if (!response.success) {
        throw new Error(response.error || 'Failed to fetch recent shows for new show detection');
      }
      
      const shows = response.data?.shows || [];
      debugLog(`✅ useNewShowDetection: Retrieved ${shows.length} recent shows`);
      
      return shows;
    },
    // Shorter cache time for new show detection
    staleTime: cacheStrategy.staleTime,
    gcTime: cacheStrategy.gcTime,
    refetchOnWindowFocus: cacheStrategy.refetchOnWindowFocus,
    refetchOnReconnect: cacheStrategy.refetchOnReconnect,
    retry: cacheStrategy.retry,
    retryDelay: cacheStrategy.retryDelay,
    enabled,
  });

  return {
    recentShows,
    loading: isPending,
    error: isError ? (error?.message || 'An error occurred') : null,
    refetch,
  };
};
